import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { LogoIcon, LogoText } from '../components/Logo';
import { useTranslation, Trans } from 'react-i18next';

const S = <strong className="text-white" />;
const A = <a href="mailto:support@scorinova.com" className="text-accent hover:text-accent-light transition-colors" />;

function Privacy() {
  const { t, i18n } = useTranslation(['legal', 'common']);
  return (
    <div className="min-h-screen bg-anthracite-950">
      <Helmet>
        <html lang={i18n.language} />
        <title>{t('legal:privacyTitle')}</title>
        <meta name="description" content={t('legal:privacyMetaDesc')} />
        <link rel="canonical" href="https://scorinova.com/privacy" />
      </Helmet>
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-anthracite-900/95 backdrop-blur-sm border-b border-anthracite-700">
        <div className="flex items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-3">
            <LogoIcon className="w-10 h-10" />
            <div>
              <LogoText size="text-xl" />
              <p className="text-[10px] text-gray-500 uppercase tracking-wider">{t('common:aiFootballPredictions')}</p>
            </div>
          </Link>
          <Link
            to="/"
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white hover:bg-anthracite-800 transition-all"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            {t('common:back')}
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="pt-28 pb-16 px-4">
        <div className="max-w-3xl mx-auto">
          <div className="bg-anthracite-900 rounded-2xl p-8 md:p-12 border border-anthracite-700">

            <h1 className="text-3xl font-bold text-white mb-2">{t('legal:privacyHeading')}</h1>
            <p className="text-sm text-gray-500 mb-10">{t('legal:lastUpdated')}</p>

            <div className="space-y-8 text-gray-300 text-[15px] leading-relaxed">

              {/* 1 */}
              <section>
                <h2 className="text-lg font-semibold text-white mb-3">{t('legal:p1Head')}</h2>
                <p>{t('legal:p1P1')}</p>
              </section>

              {/* 2 */}
              <section>
                <h2 className="text-lg font-semibold text-white mb-3">{t('legal:p2Head')}</h2>
                <p className="mb-3">{t('legal:p2P1')}</p>

                <h3 className="text-sm font-semibold text-white mt-4 mb-2">{t('legal:p2Sub1')}</h3>
                <ul className="list-disc list-inside space-y-1.5 text-gray-400 ml-2">
                  <li>{t('legal:p2S1Li1')}</li>
                  <li>{t('legal:p2S1Li2')}</li>
                  <li>{t('legal:p2S1Li3')}</li>
                  <li>{t('legal:p2S1Li4')}</li>
                </ul>

                <h3 className="text-sm font-semibold text-white mt-4 mb-2">{t('legal:p2Sub2')}</h3>
                <ul className="list-disc list-inside space-y-1.5 text-gray-400 ml-2">
                  <li>{t('legal:p2S2Li1')}</li>
                  <li>{t('legal:p2S2Li2')}</li>
                  <li>{t('legal:p2S2Li3')}</li>
                  <li>{t('legal:p2S2Li4')}</li>
                </ul>

                <h3 className="text-sm font-semibold text-white mt-4 mb-2">{t('legal:p2Sub3')}</h3>
                <ul className="list-disc list-inside space-y-1.5 text-gray-400 ml-2">
                  <li>{t('legal:p2S3Li1')}</li>
                  <li>{t('legal:p2S3Li2')}</li>
                  <li>{t('legal:p2S3Li3')}</li>
                </ul>
              </section>

              {/* 3 */}
              <section>
                <h2 className="text-lg font-semibold text-white mb-3">{t('legal:p3Head')}</h2>
                <ul className="list-disc list-inside space-y-1.5 text-gray-400 ml-2">
                  <li>{t('legal:p3Li1')}</li>
                  <li>{t('legal:p3Li2')}</li>
                  <li>{t('legal:p3Li3')}</li>
                  <li>{t('legal:p3Li4')}</li>
                  <li>{t('legal:p3Li5')}</li>
                  <li>{t('legal:p3Li6')}</li>
                </ul>
              </section>

              {/* 4 */}
              <section>
                <h2 className="text-lg font-semibold text-white mb-3">{t('legal:p4Head')}</h2>
                <p className="mb-3"><Trans i18nKey="legal:p4P1" components={{ s: S }} /></p>
                <ul className="list-disc list-inside space-y-1.5 text-gray-400 ml-2">
                  <li>{t('legal:p4Li1')}</li>
                  <li>{t('legal:p4Li2')}</li>
                  <li>{t('legal:p4Li3')}</li>
                  <li>{t('legal:p4Li4')}</li>
                </ul>
                <p className="mt-3">{t('legal:p4P2')}</p>
              </section>

              {/* 5 */}
              <section>
                <h2 className="text-lg font-semibold text-white mb-3">{t('legal:p5Head')}</h2>
                <p className="mb-3">{t('legal:p5P1')}</p>
                <ul className="list-disc list-inside space-y-1.5 text-gray-400 ml-2">
                  <li><Trans i18nKey="legal:p5Li1" components={{ s: S }} /></li>
                  <li><Trans i18nKey="legal:p5Li2" components={{ s: S }} /></li>
                  <li><Trans i18nKey="legal:p5Li3" components={{ s: S }} /></li>
                  <li><Trans i18nKey="legal:p5Li4" components={{ s: S }} /></li>
                </ul>
                <p className="mt-3">{t('legal:p5P2')}</p>
              </section>

              {/* 6 */}
              <section>
                <h2 className="text-lg font-semibold text-white mb-3">{t('legal:p6Head')}</h2>
                <p>{t('legal:p6P1')}</p>
              </section>

              {/* 7 */}
              <section>
                <h2 className="text-lg font-semibold text-white mb-3">{t('legal:p7Head')}</h2>
                <p className="mb-3">{t('legal:p7P1')}</p>
                <ul className="list-disc list-inside space-y-1.5 text-gray-400 ml-2">
                  <li><Trans i18nKey="legal:p7Li1" components={{ s: S }} /></li>
                  <li><Trans i18nKey="legal:p7Li2" components={{ s: S }} /></li>
                  <li><Trans i18nKey="legal:p7Li3" components={{ s: S }} /></li>
                  <li><Trans i18nKey="legal:p7Li4" components={{ s: S }} /></li>
                  <li><Trans i18nKey="legal:p7Li5" components={{ s: S }} /></li>
                  <li><Trans i18nKey="legal:p7Li6" components={{ s: S }} /></li>
                  <li><Trans i18nKey="legal:p7Li7" components={{ s: S }} /></li>
                </ul>
                <p className="mt-3"><Trans i18nKey="legal:p7P2" components={{ a: A }} /></p>
              </section>

              {/* 8 */}
              <section>
                <h2 className="text-lg font-semibold text-white mb-3">{t('legal:p8Head')}</h2>
                <p>{t('legal:p8P1')}</p>
              </section>

              {/* 9 */}
              <section>
                <h2 className="text-lg font-semibold text-white mb-3">{t('legal:p9Head')}</h2>
                <p>{t('legal:p9P1')}</p>
              </section>

              {/* 10 */}
              <section>
                <h2 className="text-lg font-semibold text-white mb-3">{t('legal:p10Head')}</h2>
                <p>{t('legal:p10P1')}</p>
              </section>

              {/* 11 */}
              <section>
                <h2 className="text-lg font-semibold text-white mb-3">{t('legal:p11Head')}</h2>
                <p>{t('legal:p11P1')}</p>
              </section>

              {/* 12 */}
              <section>
                <h2 className="text-lg font-semibold text-white mb-3">{t('legal:p12Head')}</h2>
                <p><Trans i18nKey="legal:p12P1" components={{ a: A }} /></p>
              </section>

            </div>
          </div>

          {/* Footer links */}
          <div className="mt-8 text-center text-xs text-gray-600 space-x-4">
            <Link to="/terms" className="hover:text-accent transition-colors">{t('common:termsAndConditions')}</Link>
            <span>|</span>
            <Link to="/signup" className="hover:text-accent transition-colors">{t('common:createAccount')}</Link>
          </div>
        </div>
      </main>
    </div>
  );
}

export default Privacy;
